import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import ArrowDown from "@/assets/dropdown_arrow_dark_blue.svg"
import { styles } from "@/components/common/Buttons/Button"
import { DROPDOWN_VARIANTS } from "@/components/common/Dropdown"
import { getCssSizeValue } from "@/utils/utils"
import { BREAKPOINTS } from "@/styles/themeSettings"

const DropdownWrapper = styled.div`
	position: relative;
	display: inline-block;
	z-index: 10;
	width: 100%;

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		width: 100%;
		max-width: ${getCssSizeValue(250, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		max-width: unset;
	}

	svg {
		position: absolute;
		right: 15px;
		top: calc(50% - 5px);
		pointer-events: none;
		transition: transform 0.25s ease-in-out;
		transform: rotate(0);
	}

	&.opened {
		svg {
			transition: transform 0.25s ease-in-out;
			transform: rotate(180deg);
		}
	}
`

const Selected = styled.div`
	border-radius: 6px;
	background: ${({ theme }) => theme.colors.white};
	cursor: pointer;
	min-width: ${getCssSizeValue(314)};
	display: flex;
	justify-content: flex-start !important;
	text-align: left;
	color: ${({ theme }) => theme.colors.richBlack};
	position: relative;
	font-weight: ${({ theme }) => theme.fontWeight.bold} !important;
	${styles};
	width: 100%;

	&.primary {
		svg {
			path {
				fill: white;
			}
		}
	}

	&.no-border {
		border: none !important;
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		padding: ${getCssSizeValue(14, BREAKPOINTS.TABLET)} ${getCssSizeValue(54, BREAKPOINTS.TABLET)}
			${getCssSizeValue(14, BREAKPOINTS.TABLET)} ${getCssSizeValue(17, BREAKPOINTS.TABLET)} !important;
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding: ${getCssSizeValue(14)} ${getCssSizeValue(20)} ${getCssSizeValue(14)} ${getCssSizeValue(17)} !important;
	}
`

const Options = styled.ul`
	list-style: none;
	margin: 0;
	z-index: 1;
	padding: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)} ${getCssSizeValue(16, BREAKPOINTS.MOBILE)} !important;
	border-radius: 6px;
	top: calc(100% + 6px);
	position: absolute;
	width: 100%;
	overflow-y: auto;
	opacity: 0;
	transition:
		opacity 0.3s ease-in-out 0.2s,
		transform 0.1s ease-in-out;
	transform: scaleY(0);
	transform-origin: top;
	background-color: var(--supp-color, transparent);
	color: var(--color, white);
	max-height: ${getCssSizeValue(200, BREAKPOINTS.MOBILE)};

	&.primary {
		--color: ${({ theme }) => theme.colors.white};
		--supp-color: ${({ theme }) => theme.colors.chilliRed};
		border: none;
	}

	&.secondary {
		--color: ${({ theme }) => theme.colors.richBlack};
		--supp-color: ${({ theme }) => theme.colors.antiflashWhite};
		border: none;
	}

	&.tertiary {
		--color: ${({ theme }) => theme.colors.richBlack};
		--supp-color: ${({ theme }) => theme.colors.white};
		border: solid 1px var(--color, white);
	}

	&.opened {
		opacity: 1;
		transition:
			opacity 0.3s ease-in-out,
			transform 0.1s ease-in-out;
		transform: scaleY(1);
		transform-origin: top;
	}

	&.no-border {
		border: none !important;
	}

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		padding: ${getCssSizeValue(14, BREAKPOINTS.TABLET)} ${getCssSizeValue(54, BREAKPOINTS.TABLET)}
			${getCssSizeValue(14, BREAKPOINTS.TABLET)} ${getCssSizeValue(17, BREAKPOINTS.TABLET)} !important;
		max-height: ${getCssSizeValue(300, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding: ${getCssSizeValue(14)} ${getCssSizeValue(20)} ${getCssSizeValue(14)} ${getCssSizeValue(17)} !important;
		max-height: ${getCssSizeValue(300, BREAKPOINTS.DESKTOP)};
	}
`

const Option = styled.li`
	cursor: pointer;
	display: flex;
	align-items: center;
	margin-bottom: 16px;

	&:last-child {
		margin-bottom: 0;
	}

	span.amount {
		color: ${({ theme }) => theme.colors.slateGray};
		margin-left: 3px;
	}

	&.disabled {
		pointer-events: none;
		color: ${({ theme }) => theme.colors.antiflashWhite};
	}

	&.primary {
		span.amount {
			color: ${({ theme }) => theme.colors.white};
		}
	}
`

const Checkbox = styled.span`
	display: inline-block;
	width: 16px;
	height: 16px;
	margin-right: 16px;
	border: 1px solid ${({ theme }) => theme.colors.richBlack};
	background: ${({ checked, theme }) => (checked ? theme.colors.richBlack : theme.colors.white)};

	&.primary {
		border: 1px solid ${({ theme }) => theme.colors.white};
		background: ${({ checked, theme }) => (checked ? theme.colors.white : theme.colors.chilliRed)};
	}
`

const ChosenOptions = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	position: absolute;
	top: calc(100% + 23px);
	margin-left: 20px;
	width: 600px;

	&:before {
		content: "";
		background-color: ${({ theme }) => theme.colors.richBlack};
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		width: 12px;
		height: 12px;
		left: -18px;
	}

	p {
		color: ${({ theme }) => theme.colors.richBlack};
		font-weight: ${({ theme }) => theme.fontWeight.medium};
		line-height: normal !important;
		text-transform: uppercase;
		width: fit-content;
	}
`

const MultiSelect = ({
	options,
	variant = DROPDOWN_VARIANTS.PRIMARY,
	selectedValues,
	onChange,
	noBorder,
	placeholder = "Choose one",
	className = "",
	displaySelectedOptions = true,
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const ref = useRef()

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (ref.current && !ref.current.contains(e.target)) {
				setIsOpen(false)
			}
		}
		document.addEventListener("mousedown", handleClickOutside)
		return () => {
			document.removeEventListener("mousedown", handleClickOutside)
		}
	}, [ref])

	const handleSelect = (selectedOption) => {
		const newValue = selectedValues.find((item) => item.id === selectedOption.id)
			? selectedValues.filter((item) => item.id !== selectedOption.id)
			: [...selectedValues, selectedOption]
		onChange({ target: { value: newValue } })
	}
	// const getSelectedCount = () => {
	// 	return selectedValues.length === 0 ? '' : `(${selectedValues.length})`;
	// };

	const getSelectedCount = () => {
		const amount = options.reduce((total, option) => {
			if (selectedValues.some((selected) => selected.id === option.id)) {
				return total + option.amount
			}
			return total
		}, 0)

		return amount === 0 ? "" : `(${amount})`
	}

	const selectedLabels = selectedValues.map((item) => item.label)
	return (
		<DropdownWrapper ref={ref} className={`${isOpen ? "opened" : ""} ${className} multiselect `}>
			<Selected
				className={` typo-strawford typo-strawford-35 no-hover variant-${variant} ${noBorder ? "no-border" : ""}`}
				onClick={() => setIsOpen(!isOpen)}
			>
				{`${placeholder} ${getSelectedCount()}`}
				<ArrowDown />
			</Selected>
			<Options
				className={isOpen ? `opened ${variant} ${noBorder ? "no-border" : ""}` : `${variant} ${noBorder ? "no-border" : ""}`}
				open={isOpen}
			>
				{options.map((option, index) => (
					<Option
						className={`typo-strawford typo-strawford-34 ${variant} ${option.disabled ? "disabled" : ""}`}
						key={index}
						onClick={() => handleSelect(option)}
					>
						<Checkbox className={variant} checked={selectedValues.some((item) => item.id === option.id)} />
						<p>{option.label} </p>
						<span className="amount">({option.amount})</span>
					</Option>
				))}
			</Options>
			{displaySelectedOptions && selectedValues.length > 0 && !isOpen && (
				<ChosenOptions>
					<p className="typo-strawford typo-strawford-35">{selectedLabels.join(", ")}</p>
				</ChosenOptions>
			)}
		</DropdownWrapper>
	)
}

export default MultiSelect
